/**
 * useClinicalDashboard Composable
 *
 * Provides state management and data fetching for the clinical quality metrics dashboard.
 * Follows the composable pattern used throughout the frontend (e.g., useAppointmentList).
 *
 * Features:
 * - Fetches dashboard summary metrics
 * - Fetches inactive patients list
 * - Loading and error states
 * - Pagination support for inactive patients
 */

import { useState, useCallback } from 'react';
import {
  clinicalDashboardService,
  DashboardSummary,
  InactivePatient,
  RosBreakdown,
} from '../../../services/clinicalDashboardService';

export interface ClinicalDashboardState {
  /** Dashboard summary metrics */
  summary: DashboardSummary | null;
  /** List of inactive patients */
  inactivePatients: InactivePatient[];
  /** Selected patient ID for per-patient ROS view */
  selectedPatientId: number | null;
  /** Per-patient ROS completeness data */
  patientRos: RosBreakdown | null;
  /** Loading state */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Pagination for inactive patients */
  pagination: {
    currentPage: number;
    totalPages: number;
    count: number;
    perPage: number;
  };
}

const DEFAULT_PER_PAGE = 15;

/**
 * Clinical Dashboard Composable
 *
 * @example
 * ```tsx
 * const {
 *   summary,
 *   inactivePatients,
 *   loading,
 *   error,
 *   fetchDashboard,
 *   fetchInactivePatients,
 * } = useClinicalDashboard();
 *
 * useEffect(() => {
 *   fetchDashboard();
 * }, []);
 * ```
 */
export const useClinicalDashboard = () => {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [inactivePatients, setInactivePatients] = useState<InactivePatient[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(null);
  const [patientRos, setPatientRos] = useState<RosBreakdown | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    count: 0,
    perPage: DEFAULT_PER_PAGE,
  });

  /**
   * Fetch dashboard summary metrics
   *
   * @param doctorId Optional doctor ID filter (admin only)
   */
  const fetchDashboard = useCallback(async (doctorId?: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await clinicalDashboardService.getDashboardSummary(doctorId);
      setSummary(data);
    } catch (err) {
      console.error('Error fetching clinical dashboard:', err);
      setError('Error al cargar el panel de métricas clínicas');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch inactive patients list
   *
   * @param doctorId Optional doctor ID filter (admin only)
   */
  const fetchInactivePatients = useCallback(
    async (doctorId?: number) => {
      setLoading(true);
      setError(null);
      try {
        const data = await clinicalDashboardService.getInactivePatients(doctorId);
        setInactivePatients(data.patients);
        setPagination((prev) => ({
          ...prev,
          count: data.count,
          totalPages: Math.ceil(data.count / prev.perPage) || 1,
        }));
      } catch (err) {
        console.error('Error fetching inactive patients:', err);
        setError('Error al cargar la lista de pacientes inactivos');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Fetch all dashboard data
   *
   * @param doctorId Optional doctor ID filter (admin only)
   */
  const fetchAll = useCallback(async (doctorId?: number) => {
    setLoading(true);
    setError(null);
    try {
      const [summaryData, inactiveData] = await Promise.all([
        clinicalDashboardService.getDashboardSummary(doctorId),
        clinicalDashboardService.getInactivePatients(doctorId),
      ]);
      setSummary(summaryData);
      setInactivePatients(inactiveData.patients);
      setPagination((prev) => ({
        ...prev,
        count: inactiveData.count,
        totalPages: Math.ceil(inactiveData.count / prev.perPage) || 1,
      }));
    } catch (err) {
      console.error('Error fetching clinical dashboard data:', err);
      setError('Error al cargar los datos del panel clínico');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Go to specific page in inactive patients list
   */
  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: page }));
    }
  }, [pagination.totalPages]);

  /**
   * Fetch ROS completeness for a specific patient
   *
   * @param patientId Patient ID to fetch ROS data for
   */
  const fetchPatientRos = useCallback(async (patientId: number) => {
    setSelectedPatientId(patientId);
    setPatientRos(null);
    setError(null);
    try {
      const data = await clinicalDashboardService.getPatientRos(patientId);
      setPatientRos(data.ros);
    } catch (err) {
      console.error('Error fetching patient ROS:', err);
      setError('Error al cargar datos de Revisión de Sistemas del paciente');
    }
  }, []);

  /**
   * Clear selected patient and their ROS data
   */
  const clearSelectedPatient = useCallback(() => {
    setSelectedPatientId(null);
    setPatientRos(null);
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    summary,
    inactivePatients,
    selectedPatientId,
    patientRos,
    loading,
    error,
    pagination,
    fetchDashboard,
    fetchInactivePatients,
    fetchAll,
    fetchPatientRos,
    clearSelectedPatient,
    goToPage,
    clearError,
  };
};